import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { Laptop, Briefcase, GraduationCap, CheckCircle2 } from "lucide-react";

export default function TheBootcampExperienceSection() {
  const steps = [
    {
      icon: <Laptop size={28} className="text-[#d15424]" />,
      title: "Learn in Live Sessions",
      desc: "Attend interactive, live online classes led by senior professionals. No pre-recorded isolation; you learn together in a structured global cohort."
    },
    {
      icon: <CheckCircle2 size={28} className="text-[#d15424]" />,
      title: "Build Real Projects",
      desc: "Apply what you learn immediately. Build a portfolio of real-world projects that solve actual business problems at scale."
    },
    {
      icon: <GraduationCap size={28} className="text-[#d15424]" />,
      title: "Graduate & Certify",
      desc: "Earn an industry-recognized certification upon successful completion and defense of your capstone project."
    },
    {
      icon: <Briefcase size={28} className="text-[#d15424]" />,
      title: "Get Hired Globally",
      desc: "Access our exclusive job board, get resume reviews, interview prep, and direct referrals to our global hiring partners."
    }
  ];

  return (
    <Box sx={{ py: 24, bgcolor: "#0f172a", color: "white", position: "relative", overflow: "hidden" }}>
      {/* Background Tech Effects */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#d15424] opacity-[0.05] blur-[150px] rounded-full pointer-events-none"></div>

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 10 }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#d15424] animate-pulse"></span>
                <span className="text-xs font-bold text-gray-300 font-montserrat uppercase tracking-wider">The Blueprint</span>
            </div>
            
            <Typography variant="h2" sx={{ fontWeight: 900, color: "white", fontSize: { xs: "2.5rem", md: "3.5rem" }, mb: 4, lineHeight: 1.1, fontFamily: "Monserrat, sans-serif", letterSpacing: "-0.02em" }}>
              How we transform <br/>your <span className="text-[#d15424]">career trajectory.</span>
            </Typography>
            <Typography variant="body1" sx={{ color: "#94a3b8", mb: 8, lineHeight: 1.7, fontFamily: "Monserrat, sans-serif", fontSize: "1.15rem", maxWidth: "90%" }}>
              We don't just teach syntax; we engineer careers. Our pedagogy is built by world-class experts, designed to take you from beginner to a highly employable professional.
            </Typography>

            <button className="px-8 py-4 bg-[#d15424] text-white font-bold rounded-xl shadow-[0_8px_20px_rgba(209,84,36,0.25)] hover:shadow-[0_15px_30px_rgba(209,84,36,0.4)] hover:-translate-y-1 transition-all duration-300 font-montserrat text-lg border border-transparent">
              View Upcoming Cohorts
            </button>
          </motion.div>

          <div className="relative pl-4 md:pl-10">
             {/* Glowing Timeline line */}
             <div className="absolute left-[39px] md:left-[63px] top-8 bottom-8 w-[2px] bg-gradient-to-b from-transparent via-[#d15424]/50 to-transparent hidden md:block shadow-[0_0_15px_rgba(209,84,36,0.5)]"></div>
             
             <div className="space-y-12 relative">
               {steps.map((step, idx) => (
                 <motion.div 
                   key={idx}
                   initial={{ opacity: 0, x: 30 }}
                   whileInView={{ opacity: 1, x: 0 }}
                   viewport={{ once: true }}
                   transition={{ delay: idx * 0.15, duration: 0.6 }}
                   className="flex items-start gap-6 group"
                 >
                   <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-[#1e293b] border border-gray-700 flex items-center justify-center shrink-0 z-10 relative shadow-[0_4px_20px_rgba(0,0,0,0.3)] group-hover:border-[#d15424]/50 group-hover:bg-[#d15424]/10 transition-all duration-500">
                     {step.icon}
                   </div>
                   <div className="pt-1 md:pt-2">
                     <Typography variant="overline" sx={{ color: "#d15424", fontWeight: 700, fontFamily: "Monserrat, sans-serif", letterSpacing: 1.5 }}>Phase 0{idx + 1}</Typography>
                     <h4 className="text-xl md:text-2xl font-bold font-montserrat mb-3 text-gray-100 group-hover:text-white transition-colors">{step.title}</h4>
                     <p className="text-gray-400 font-montserrat text-sm md:text-base leading-relaxed max-w-sm">{step.desc}</p>
                   </div>
                 </motion.div>
               ))}
             </div>
          </div>

        </div>
      </Container>
    </Box>
  );
}
